import { getHostApi, getRequestConfigForFetch } from "../utils";
import { Address, GPSCoordinates, Pays, SearchAddress, SearchCompany } from '../interfaces-base';
import { getUserId } from '../components/mon-compte/MonCompteAction';
import { BaseForm, ProForm } from '../components/form-address/fa-interfaces';

type DefaultResponseWithMessage<T> = { message?: string; } & T;

export async function fetchCountries(): Promise<{pays: Pays[]}> {
  const response = await fetch(`${getHostApi()}/addresses/pays`, { method: 'GET', ...getRequestConfigForFetch() });
  return response.json();
}

export async function fetchValidateZipCode(isoCode: string, zipCode: string): Promise<DefaultResponseWithMessage<unknown>> {
  const response = await fetch(
    `${getHostApi()}/addresses/zip-code/validate?iso_code=${isoCode}&zip_code=${zipCode}`,
    { method: 'GET', ...getRequestConfigForFetch() }
  );
  const json = await response.json();

  if (response.status >= 400) {
    throw new Error(json.message);
  }
  return json;
}

export async function fetchValidatePhoneNumber(isoCode: string, type: 'mobile' | 'fixe', phoneNumber: string): Promise<DefaultResponseWithMessage<unknown>> {
  const config = getRequestConfigForFetch();
  const response = await fetch(
    `${getHostApi()}/addresses/phone-number/validate`,
    {
      ...config,
      method: 'POST',
      headers: {
        ...config.headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ phone_number: phoneNumber, code_pays: isoCode, type })
    }
  );
  const json = await response.json();

  if (response.status >= 400) {
    throw new Error(json.message);
  }
  return json;
}

export async function fetchValidateNumTva(isoCode: string, numTva: string, societe: string, nom: string): Promise<DefaultResponseWithMessage<unknown>> {
  const config = getRequestConfigForFetch();
  const response = await fetch(
    `${getHostApi()}/addresses/numero-tva/validate`,
    {
      ...config,
      method: 'POST',
      headers: {
        ...config.headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ numero_tva: numTva, codePays: isoCode, societe, nom })
    }
  );
  const json = await response.json();

  if (response.status >= 400) {
    throw new Error(json.message);
  }
  return json;
}

export async function fetchAddressById(id: number): Promise<{ address: Address }> {
  const response = await fetch(`${getHostApi()}/customers/${getUserId()}/addresses/${id}`, { method: 'GET', ...getRequestConfigForFetch() });
  return response.json();
}

export async function fetchAddressCoordsByStr(address: string): Promise<{ coords: GPSCoordinates }> {
  const response = await fetch(`${getHostApi()}/customers/${getUserId()}/addresses/coords?addr=${address}`, { method: 'GET', ...getRequestConfigForFetch() });
  return response.json();
}

export async function fetchSearchCompany(search: string, limit: number = 10, signal: AbortSignal|undefined = undefined): Promise<SearchCompany[]> {
  const response = await fetch(
    `${getHostApi()}/addresses/search-company?q=${search}&limit=${limit}`,
    {
      ...getRequestConfigForFetch(signal),
      method: 'GET'
    }
  );
  if (response.status >= 400) {
    throw new Error('Bad response from server');
  }
  return response.json();
}

export async function fetchSearchAddress(search: string, limit: number = 10, signal: AbortSignal|undefined = undefined): Promise<SearchAddress[]> {
  const response = await fetch(
    `${getHostApi()}/addresses/search-address?q=${search}&limit=${limit}`,
    {
      ...getRequestConfigForFetch(signal),
      method: 'GET'
    }
  );
  if (response.status >= 400) {
    throw new Error('Bad response from server');
  }
  return response.json();
}

export async function updateAddress(id: number, form: BaseForm & ProForm): Promise<unknown> {
  const config = getRequestConfigForFetch();
  const response = await fetch(`${getHostApi()}/customers/${getUserId()}/addresses/${id}`, {
    ...config,
    method: 'PUT',
    headers: {
      ...config.headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(form)
  });
  const json = await response.json();

  if (response.status >= 400) {
    throw new Error(json.message);
  }
  return json;
}

export async function addAddress(form: BaseForm & ProForm): Promise<unknown> {
  const config = getRequestConfigForFetch();
  const response = await fetch(`${getHostApi()}/customers/${getUserId()}/addresses`, {
    ...config,
    method: 'POST',
    headers: {
      ...config.headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(form)
  });
  const json = await response.json();

  if (response.status >= 400) {
    throw new Error(json.message);
  }
  return json;
}
